import { isoDateTime, z } from "../zod.js";
import { Problem } from "../common/problem.js";
import { PlanningDate, PlanningMonday, PlanningQty } from "./schemas.js";

// Module 03 planning: the schedule contract (spec 03 §2.15 to §2.19c, §3.5,
// §3.6, §4.6 to §4.8 and §5 rows 54 to 71; D-397 to D-404, D-406). P3-I-2
// built rows 54 to 66 without the moves, and P3-I-3 adds the overload's
// ranked moves, apply-move, the drag preview and commit, and the directives
// (rows 66 to 71, D-402, D-403). Times are ISO instants with an offset on
// whole minutes, minutes are integers, and hours are decimal strings with two
// places. The only money is a move's `cost_minor` and `value_at_risk_minor`
// with their currency, integer minor units served to a holder of
// `planning.cost.read` and absent by key for every other reader of
// the moves (§7 narrowing (b)). Lists answer `{items, next_cursor}`.

const Uuid = z.guid();
/** An instant with its offset, as the board and the runs are served (spec 03 §5). */
const Instant = isoDateTime({ offset: true });
const PageCursor = z.string().optional();
const PageLimit = z.coerce.number().int().min(1).max(200).optional();
const Count = z.number().int().min(0);
const Minutes = z.number().int().min(0);

/** Hours with two decimals, `"12.50"`. */
export const ScheduleHours = z
  .string()
  .regex(/^\d{1,9}\.\d{2}$/)
  .openapi({ example: "12.50", description: "Hours, two decimals" });

/** A percentage with one decimal, `"87.5"`. */
export const SchedulePct = z
  .string()
  .regex(/^\d{1,6}\.\d$/)
  .openapi({ example: "87.5", description: "Per cent, one decimal" });

/** A factor from 0.01 to 1.00 with two decimals, as a maintenance port answers it (D-404). */
export const AvailabilityFactor = z
  .string()
  .regex(/^[01]\.\d{2}$/)
  .openapi({ example: "1.00" });

/** The widest window a board or capacity read spans (spec 03 §5 rows 60 and 65). */
export const SCHEDULE_WINDOW_MAX_DAYS = 84;
/** The most rows one setup-matrix replace carries (spec 03 §5 row 55). */
export const SETUP_MATRIX_MAX_ROWS = 2000;
/** The longest list a stats field, a diff or an overload row serves beside its exact count. */
export const SCHEDULE_LIST_CAP = 200;

// --- enums --------------------------------------------------------------------------

/** `sandbox` is a scenario's schedule, which row 61 alone serves (J-4, D-471). */
export const ScheduleKind = z.enum(["live", "sandbox"]);
/** The statuses a live schedule holds, which row 59 filters on. */
export const LiveScheduleStatus = z.enum(["draft", "published", "superseded", "discarded"]);
/** A sandbox's status is `sandbox` and nothing else (D-471 ruling 1). */
export const ScheduleStatus = z.enum(["draft", "published", "superseded", "discarded", "sandbox"]);
/**
 * What solved a schedule (§2.16). `commit_move` and `apply_move` are P3-I-3's
 * writers; `scenario` is a sandbox's (J-4).
 */
export const ScheduleTrigger = z.enum([
  "cron",
  "event",
  "manual",
  "commit_move",
  "apply_move",
  "scenario",
]);
/**
 * What started a run (§2.19). `drag_preview`, `commit_move` and `apply_move`
 * are P3-I-3's; `ctp_probe` is P3-J J-1's capable-to-promise probe (D-461
 * ruling 11); `scenario` is J-4's sandbox solve, served only to holders of
 * `planning.scenario.manage` (D-471 ruling 2).
 */
export const ScheduleRunTrigger = z.enum([
  "cron",
  "event",
  "manual",
  "drag_preview",
  "commit_move",
  "apply_move",
  "ctp_probe",
  "scenario",
]);
/** The runs row 57's `solves_only` serves: the solves that may write a schedule (D-461 ruling 11). */
export const SCHEDULE_SOLVE_TRIGGERS = [
  "cron",
  "event",
  "manual",
  "commit_move",
  "apply_move",
] as const;
export const ScheduleRunStatus = z.enum(["running", "succeeded", "failed"]);
/** `schedule_runs.error` (§2.19), a key the workbench renders. */
export const ScheduleRunError = z.enum([
  "LEASE_EXPIRED",
  "SHUTDOWN",
  "TOO_MANY_OPERATIONS",
  "COMPUTE_FAILED",
]);
/** `scenario_order_op` is a sandbox's `add_order` step, which names no order (J-4, D-472). */
export const ScheduleOperationSource = z.enum([
  "wo_operation",
  "planned_order_op",
  "scenario_order_op",
]);
export const SchedulePlacement = z.enum(["placed", "unplaced"]);
/** Why an operation was not placed (§2.17, D-397 ruling 7). */
export const ScheduleUnplacedReason = z.enum([
  "work_center_uncalendared",
  "work_center_blocked",
  "no_capacity",
  "on_hold",
  "predecessor_unplaced",
  "no_routing",
]);
/** Why the solver did not choose an operation's time (§2.17). */
export const ScheduleFixed = z.enum(["in_progress", "pin_position", "fenced"]);
/** `open → superseded` with its schedule, or `open → move_applied` by apply-move (§3.5, D-402 ruling 5). */
export const ScheduleOverloadStatus = z.enum(["open", "superseded", "move_applied"]);
/** The auto-publish tests that can hold a draft (§2.16, D-399 ruling 2). */
export const AutoPublishHeldBy = z.enum([
  "first_schedule",
  "auto_publish_off",
  "ops_moved",
  "new_overloads",
  "pins_released",
]);
/** Where a maintenance port's answer came from; `unbound` until module 08 binds it (D-404). */
export const MaintenancePortSource = z.enum(["unbound", "maintenance"]);
export const CapacityGrain = z.enum(["day", "week"]);

// --- §2.15 the setup matrix (rows 54 and 55) -----------------------------------------

/** One stored row, with `unused` set when no active routing step's key on the work centre names its values. */
export const SetupMatrixRow = z
  .object({
    id: Uuid,
    facet: z.string(),
    from_value: z.string(),
    to_value: z.string(),
    minutes: z.number().int().min(0).max(1440),
    unused: z.boolean(),
  })
  .openapi("SetupMatrixRow");

export const SetupMatrix = z
  .object({
    facility_id: Uuid,
    work_center_id: Uuid,
    /** The latest `updated_at` of the work centre's rows; null when it has none. */
    updated_at: Instant.nullable(),
    rows: z.array(SetupMatrixRow),
    /** Each facet the work centre's active routing steps carry, with the values they use, sorted. */
    facets_in_use: z.record(z.string(), z.array(z.string())),
  })
  .openapi("SetupMatrix");

export const SetupMatrixQuery = z.object({ facility_id: Uuid, work_center_id: Uuid });

export const SetupMatrixWorkCenterParam = z.object({ workCenterId: Uuid });

/**
 * One row of a replace. The shape is checked here, at most 1 024 characters a
 * field so a body stays bounded; the grammar, the range and the duplicates
 * answer 422 `SETUP_MATRIX_INVALID` naming each row (spec 03 §5 row 55).
 */
export const SetupMatrixPutRow = z
  .object({
    facet: z.string().max(1024),
    from_value: z.string().max(1024),
    to_value: z.string().max(1024),
    minutes: z.number().int(),
  })
  .strict()
  .openapi("SetupMatrixPutRow");

export const SetupMatrixPutRequest = z
  .object({
    /** The `updated_at` the caller read, null when it read no rows. A stale value answers 409. */
    expected_updated_at: Instant.nullable(),
    rows: z.array(SetupMatrixPutRow).max(SETUP_MATRIX_MAX_ROWS),
  })
  .strict()
  .openapi("SetupMatrixPutRequest");

export const SetupMatrixRowIssueReason = z.enum([
  "facet_malformed",
  "value_malformed",
  "same_value",
  "duplicate_row",
  "minutes_out_of_range",
]);

export const SetupMatrixRowIssue = z
  .object({ index: z.number().int().min(0), reasons: z.array(SetupMatrixRowIssueReason) })
  .openapi("SetupMatrixRowIssue");

/** 422 `SETUP_MATRIX_INVALID` lists every failing row, ordered by index. */
export const SetupMatrixInvalidProblem = Problem.extend({
  rows: z.array(SetupMatrixRowIssue).optional(),
}).openapi("SetupMatrixInvalidProblem");

/** 409 `SETUP_MATRIX_CHANGED` carries the work centre's current `updated_at`. */
export const SetupMatrixConflictProblem = Problem.extend({
  updated_at: Instant.nullable().optional(),
}).openapi("SetupMatrixConflictProblem");

/**
 * The kernel's changeover between two setup keys on a work centre, with the
 * rows that priced it (§4.8's test panel and §4.6's block drawer, decided in
 * P3-I-2's build, 2026-10-12): the web app cannot import the scheduler, so
 * it asks the server for the kernel's own answer.
 */
export const SetupMatrixExplainQuery = z.object({
  facility_id: Uuid,
  work_center_id: Uuid,
  /** The lane's previous key; omitted or empty for an unknown one. */
  from_key: z.string().max(512).optional(),
  /** The next operation's key; omitted or empty for none. */
  to_key: z.string().max(512).optional(),
  /** The step's static setup minutes, 0 when omitted. */
  static_minutes: z
    .string()
    .regex(/^\d{1,4}(\.\d{1,2})?$/)
    .optional(),
});

export const SetupMatrixExplanation = z
  .object({
    work_center_id: Uuid,
    /** `missing_key`, `same_key`, `matrix` or `matrix_incomplete` (§2.15). */
    basis: z.enum(["missing_key", "same_key", "matrix", "matrix_incomplete"]),
    /** The changeover in effective minutes, decimal text. */
    minutes: z.string(),
    /** The canonical keys compared; null for a missing one. */
    from_key: z.string().nullable(),
    to_key: z.string().nullable(),
    /** Each facet whose value differs, with the row that priced it or null. */
    facets: z.array(
      z.object({
        facet: z.string(),
        from: z.string(),
        to: z.string(),
        row: z
          .object({ from_value: z.string(), to_value: z.string(), minutes: z.number().int() })
          .nullable(),
      }),
    ),
  })
  .openapi("SetupMatrixExplanation");

// --- §2.16 the schedule header (rows 59 to 64) ----------------------------------------

/** `schedules.kpis` (§2.16, D-397 ruling 11): numbers only, no money. */
export const ScheduleKpis = z
  .object({
    late_orders: Count,
    total_late_days: Count,
    makespan_hours: ScheduleHours,
    total_setup_minutes: Minutes,
    utilization: z.array(z.object({ work_center_id: Uuid, pct: SchedulePct })),
    overloads: Count,
    unplaced: Count,
    batch_pulls: Count,
  })
  .openapi("ScheduleKpis");

/** `schedules.auto_publish` (§2.16, D-399 ruling 2): the verdict on an event or nightly solve. */
export const ScheduleAutoPublish = z
  .object({
    published: z.boolean(),
    urgent: z.boolean(),
    held_by: z.array(AutoPublishHeldBy),
    moved_ops: Count,
    new_overloads: Count,
    released_pins: Count,
  })
  .openapi("ScheduleAutoPublish");

export const ScheduleHeader = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    kind: ScheduleKind,
    version: z.number().int().min(1),
    status: ScheduleStatus,
    /** The schedule published when the run read its snapshot; null for a facility's first and once purged. */
    base_schedule_id: Uuid.nullable(),
    schedule_run_id: Uuid,
    trigger: ScheduleTrigger,
    horizon_start: PlanningDate,
    horizon_days: z.number().int().min(7).max(84),
    solved_at: Instant,
    heuristic_version: z.string(),
    facts_hash: z.string(),
    kpis: ScheduleKpis,
    auto_publish: ScheduleAutoPublish.nullable(),
    published_by: Uuid.nullable(),
    published_at: Instant.nullable(),
    superseded_at: Instant.nullable(),
    discarded_at: Instant.nullable(),
    created_by: Uuid.nullable(),
    created_at: Instant,
  })
  .openapi("ScheduleHeader");

export const ScheduleIdParam = z.object({ id: Uuid });

export const ScheduleListQuery = z.object({
  facility_id: Uuid,
  status: LiveScheduleStatus.optional(),
  cursor: PageCursor,
  limit: PageLimit,
});

export const ScheduleListResponse = z
  .object({ items: z.array(ScheduleHeader), next_cursor: z.string().nullable() })
  .openapi("ScheduleListResponse");

/** `POST /v1/schedules/{id}/publish` and `/discard` take an empty body (spec 03 §5 rows 63 and 64). */
export const ScheduleDecisionRequest = z.object({}).strict().openapi("ScheduleDecisionRequest");

/**
 * 409 on a publish or a discard: `SCHEDULE_NOT_DRAFT` carries the schedule's
 * status and the facility's newest draft, null when none waits (D-399 ruling
 * 2); `SCHEDULE_SUPERSEDED` carries neither.
 */
export const ScheduleConflictProblem = Problem.extend({
  schedule_status: LiveScheduleStatus.optional(),
  newest_draft_id: Uuid.nullable().optional(),
}).openapi("ScheduleConflictProblem");

// --- the board (rows 60 and 61) ------------------------------------------------------

/**
 * An order's label for its reader (§7 narrowing (a)). Every holder of
 * `planning.schedule.read` reads the product, quantity, step, work centre and
 * times. `order_no` is null for a planned order of a kind the reader's lineage
 * does not read at the facility, which the board renders "Planned order";
 * `order_link` is true when the order's own door opens it for the reader.
 */
export const ScheduleOrderLabel = z
  .object({
    order_no: z.string().nullable(),
    order_link: z.boolean(),
    /** A pegged order's sales line as module 09 renders it for the reader; null when unpegged. */
    sales_order: z
      .object({
        order_id: Uuid.nullable(),
        order_no: z.string().nullable(),
        line_no: z.number().int().min(1).nullable(),
        customer_name: z.string().nullable(),
      })
      .nullable(),
  })
  .openapi("ScheduleOrderLabel");

export const ScheduleOperation = z
  .object({
    id: Uuid,
    op_ref: z.string(),
    source_kind: ScheduleOperationSource,
    work_order_id: Uuid.nullable(),
    wo_operation_id: Uuid.nullable(),
    planned_order_id: Uuid.nullable(),
    routing_step_id: Uuid,
    label: ScheduleOrderLabel,
    product_id: Uuid,
    /** Module 01's product code and name, read at serve time; null when the product is gone. */
    product_sku: z.string().nullable(),
    product_name: z.string().nullable(),
    qty: PlanningQty,
    uom: z.string(),
    step_no: z.number().int(),
    step_name: z.string(),
    work_center_id: Uuid,
    /**
     * The lanes the board's drag and move form may name (§3.6): the lane the
     * operation is on, its routing step's work centre and the step's
     * alternates that are active work centres of the facility, sorted. The
     * server still decides, with 422 `WORK_CENTER_NOT_ALLOWED` (D-416).
     */
    allowed_work_center_ids: z.array(Uuid),
    setup_key: z.string().nullable(),
    placement: SchedulePlacement,
    unplaced_reason: ScheduleUnplacedReason.nullable(),
    planned_start: Instant.nullable(),
    planned_end: Instant.nullable(),
    setup_minutes: Minutes,
    run_minutes: Minutes,
    sequence_index: z.number().int().min(1).nullable(),
    batch_key: z.string().nullable(),
    fixed: ScheduleFixed.nullable(),
    pin_released: z.boolean(),
    is_outsourced: z.boolean(),
    priority: z.number().int().min(1).max(5),
    due_date: PlanningDate.nullable(),
    is_late: z.boolean(),
  })
  .openapi("ScheduleOperation");

/** One stretch of a lane's working time in the window, at its rate (§3.3a). */
export const ScheduleLanePiece = z
  .object({
    start: Instant,
    end: Instant,
    shift_code: z.string().nullable(),
    efficiency_pct: z.number().int(),
    /** The calendar exception's capacity factor over the stretch, `"1.00"` when none applies. */
    factor: z.string().regex(/^\d{1,3}\.\d{2}$/),
  })
  .openapi("ScheduleLanePiece");

export const ScheduleLane = z
  .object({
    work_center_id: Uuid,
    code: z.string(),
    name: z.string(),
    /** Placed setup and run over capacity across the horizon (§2.16's KPI); null with no capacity. */
    utilization_pct: SchedulePct.nullable(),
    /** `MAINTENANCE_AVAILABILITY_PORT`'s answer (D-404): `1.00` and `unbound` until module 08 binds it. */
    availability: z.object({ factor: AvailabilityFactor, source: MaintenancePortSource }),
    /** `MAINTENANCE_GATE_PORT`'s answer (D-404): not blocked and `unbound` until module 08 binds it. */
    gate: z.object({ hard_blocked: z.boolean(), source: MaintenancePortSource }),
    pieces: z.array(ScheduleLanePiece),
  })
  .openapi("ScheduleLane");

/** Holds a `from`/`to` window to an ordered span of at most 84 days. */
function windowWithin(
  q: { from?: string | undefined; to?: string | undefined },
  ctx: z.RefinementCtx,
): void {
  if (q.from === undefined || q.to === undefined) return;
  if (q.to < q.from) {
    ctx.addIssue({
      code: "custom",
      path: ["to"],
      message: "to may not be before from",
      params: { reason: "to_before_from" },
    });
    return;
  }
  const days = (Date.parse(`${q.to}T00:00:00Z`) - Date.parse(`${q.from}T00:00:00Z`)) / 86_400_000;
  if (days + 1 > SCHEDULE_WINDOW_MAX_DAYS) {
    ctx.addIssue({
      code: "custom",
      path: ["to"],
      message: `a window spans at most ${String(SCHEDULE_WINDOW_MAX_DAYS)} days`,
      params: { reason: "window_too_wide" },
    });
  }
}

/**
 * The board read (rows 60 and 61). `from` and `to` are facility business
 * dates, both inclusive, at most 84 days apart, by default the schedule's
 * horizon; a lane's pieces and its placed operations are the ones meeting the
 * window. Operations page by lane and sequence.
 */
const boardWindow = {
  from: PlanningDate.optional(),
  to: PlanningDate.optional(),
  work_center_id: Uuid.optional(),
  cursor: PageCursor,
  limit: PageLimit,
};

export const ScheduleBoardQuery = z.object(boardWindow).superRefine(windowWithin);

export const ScheduleLiveQuery = z
  .object({ facility_id: Uuid, ...boardWindow })
  .superRefine(windowWithin);

export const ScheduleBoard = z
  .object({
    /**
     * Row 60: the published schedule, null before the facility's first
     * publish. Row 61: the version asked for.
     */
    schedule: ScheduleHeader.nullable(),
    /** The facility's waiting draft, null when none waits or it is the version read. */
    waiting_draft: ScheduleHeader.nullable(),
    lanes: z.array(ScheduleLane),
    operations: z.array(ScheduleOperation),
    /**
     * The unplaced operations, on the first page only (no cursor), at most
     * 200 in lane and key order beside their exact count.
     */
    unplaced: z.array(ScheduleOperation),
    unplaced_count: Count,
    next_cursor: z.string().nullable(),
  })
  .openapi("ScheduleBoard");

// --- the diff (row 62) ---------------------------------------------------------------

export const ScheduleMovedOperation = z
  .object({
    op_ref: z.string(),
    label: ScheduleOrderLabel,
    old_start: Instant.nullable(),
    new_start: Instant.nullable(),
    old_work_center_id: Uuid.nullable(),
    new_work_center_id: Uuid.nullable(),
    /** Whole minutes from the old start to the new; null when either side is unplaced. */
    delta_minutes: z.number().int().nullable(),
  })
  .openapi("ScheduleMovedOperation");

export const ScheduleOverloadRef = z
  .object({
    work_center_id: Uuid,
    week_start: PlanningMonday,
    overload_hours: ScheduleHours,
    /** The row in the schedule it belongs to: the new schedule's for a new one, the base's for a cleared one. */
    schedule_overload_id: Uuid,
  })
  .openapi("ScheduleOverloadRef");

/**
 * A version against its base (row 62): each operation present in both whose
 * start moved by a minute or more or whose lane changed, and the overloads
 * new to it and cleared by it, each list at most 200 beside its exact count.
 * A first schedule, or one whose base is purged, reads every list empty.
 */
export const ScheduleDiff = z
  .object({
    schedule_id: Uuid,
    base_schedule_id: Uuid.nullable(),
    moved_ops: z.array(ScheduleMovedOperation),
    moved_ops_count: Count,
    new_overloads: z.array(ScheduleOverloadRef),
    new_overloads_count: Count,
    cleared_overloads: z.array(ScheduleOverloadRef),
    cleared_overloads_count: Count,
    auto_publish: ScheduleAutoPublish.nullable(),
  })
  .openapi("ScheduleDiff");

// --- the runs (rows 56 to 58) --------------------------------------------------------

/**
 * `schedule_runs.stats` (§2.19). A running or failed run serves what it
 * stored, so every field is optional; `pins_released` holds at most 200
 * operation references beside their exact count (D-376 ruling 2).
 */
export const ScheduleRunStats = z
  .object({
    ops_scheduled: Count,
    ops_unplaced: Count,
    overloads: Count,
    batch_pulls: Count,
    moves_scored: Count,
    moves_unscored: Count,
    stale_base: z.boolean(),
    pins_released: z.array(z.string()),
    pins_released_count: Count,
    directives_expired: Count,
    /** Placements and unplacements the generation made. */
    decisions: Count,
    /** Whether the claim carried an urgent arm (D-398 ruling 2). */
    urgent: z.boolean(),
    /** Whether the run's draft published itself under §3.5's tests. */
    auto_published: z.boolean(),
    /** A facility with nothing to schedule and no published schedule writes nothing. */
    empty: z.boolean(),
    /** Firmed manufacture orders with no routing, listed `no_routing` (§3.5), at most 200. */
    no_routing_orders: z.array(Uuid),
    no_routing_count: Count,
    /**
     * A `ctp_probe` run's outcome (D-461 ruling 11): the promise it quoted,
     * the synthetic order's completion, whether it was left unplaced, and the
     * passes the probe solved.
     */
    ctp: z.object({
      promise_no: z.string().nullable(),
      completion: z.string().nullable(),
      unplaced: z.boolean(),
      passes: z.number().int().min(0),
    }),
  })
  .partial()
  .openapi("ScheduleRunStats");

export const ScheduleRun = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    trigger: ScheduleRunTrigger,
    status: ScheduleRunStatus,
    /** The schedule it wrote; null for a short circuit, a stale base, a failure and once purged. */
    schedule_id: Uuid.nullable(),
    /** The draft or published schedule whose facts hash it matched (§3.5's 30-minute short circuit). */
    short_circuit_schedule_id: Uuid.nullable(),
    /** The published schedule its snapshot read; null for a facility's first. */
    base_schedule_id: Uuid.nullable(),
    heuristic_version: z.string().nullable(),
    facts_hash: z.string().nullable(),
    duration_ms: z.number().int().min(0).nullable(),
    stats: ScheduleRunStats,
    error: ScheduleRunError.nullable(),
    /** Keys and ids that explain `error`, never words. */
    error_detail: z.record(z.string(), z.unknown()).nullable(),
    started_at: Instant,
    finished_at: Instant.nullable(),
    created_by: Uuid.nullable(),
  })
  .openapi("ScheduleRun");

export const ScheduleRunIdParam = z.object({ id: Uuid });

export const ScheduleRunListQuery = z.object({
  facility_id: Uuid,
  status: ScheduleRunStatus.optional(),
  trigger: ScheduleRunTrigger.optional(),
  /**
   * `true` serves the solves alone (`cron`, `event`, `manual`, `commit_move`
   * and `apply_move`), so neither a probe nor a drag preview reads as the
   * facility's last solve (D-461 ruling 11). Default `false`.
   */
  solves_only: z.enum(["true", "false"]).optional(),
  cursor: PageCursor,
  limit: PageLimit,
});

/** The facility's armed solve request (§2.19c), the debounce a person or an event set. */
export const ScheduleSolveRequestState = z
  .object({
    first_requested_at: Instant,
    due_at: Instant,
    last_trigger: z.string(),
    trigger_count: z.number().int().min(1),
    urgent: z.boolean(),
    /** The person whose solve waits behind a running one; null for an event's request. */
    manual_requested_by: Uuid.nullable(),
    failed_attempts: Count,
    next_attempt_at: Instant.nullable(),
  })
  .openapi("ScheduleSolveRequestState");

export const ScheduleRunListResponse = z
  .object({
    items: z.array(ScheduleRun),
    next_cursor: z.string().nullable(),
    /** The request waiting to run, null when none is armed. */
    pending_request: ScheduleSolveRequestState.nullable(),
    /** Solves that ended `failed` since the facility's last success (D-398 ruling 5). */
    consecutive_failures: Count,
    /** The facility's last nightly solve claim (`planning_settings.nightly_solved_on`). */
    nightly_solved_on: PlanningDate.nullable(),
  })
  .openapi("ScheduleRunListResponse");

export const ScheduleSolveRequest = z
  .object({ facility_id: Uuid })
  .strict()
  .openapi("ScheduleSolveRequest");

/** 202: a new run, or the running one with `queued: true` and a request armed due now (§3.5 step 1). */
export const ScheduleSolveAccepted = z
  .object({ schedule_run_id: Uuid, status: ScheduleRunStatus, queued: z.boolean() })
  .openapi("ScheduleSolveAccepted");

// --- capacity by interval (row 65) ---------------------------------------------------

export const CapacityLoadQuery = z
  .object({
    facility_id: Uuid,
    /** First facility business date, inclusive. */
    from: PlanningDate,
    /** Last facility business date, inclusive, at most 84 days after `from`. */
    to: PlanningDate,
    grain: CapacityGrain,
    work_center_id: Uuid.optional(),
    /** A version of the facility's; the published schedule when omitted. */
    schedule_id: Uuid.optional(),
  })
  .superRefine(windowWithin);

/**
 * One work centre and period of §2.19a's load. A week row sums its dates and
 * starts on the Monday; `overload_minutes` is the week's §2.18 overload on a
 * week row, and on a day row the week's overload on its peak date and 0 on
 * every other date, so the two grains add up to the same total.
 */
export const CapacityLoadRow = z
  .object({
    work_center_id: Uuid,
    period_start: PlanningDate,
    capacity_minutes: Minutes,
    setup_minutes: Minutes,
    run_minutes: Minutes,
    due_minutes: Minutes,
    proposed_minutes: Minutes,
    overload_minutes: Minutes,
  })
  .openapi("CapacityLoadRow");

export const CapacityLoad = z
  .object({
    /** The schedule read; null when the facility has no published schedule and none was named. */
    schedule_id: Uuid.nullable(),
    grain: CapacityGrain,
    from: PlanningDate,
    to: PlanningDate,
    rows: z.array(CapacityLoadRow),
  })
  .openapi("CapacityLoad");

// --- §2.18 the moves (row 66 from P3-I-3, D-402) ---------------------------------------

export const ScheduleMoveKind = z.enum(["overtime", "alternate_work_center", "push_order"]);
/** Whether the runner scored the overload's moves after the publish, or a newer publish abandoned the scoring (D-402 ruling 3). */
export const ScheduleMovesStatus = z.enum(["pending", "scored", "abandoned"]);
/** Why a move's money is null: a rate or value in another currency, or none (D-402 ruling 2). */
export const ScheduleMoveUnpricedReason = z.enum([
  "currency_mismatch",
  "no_rate",
  "no_standard_cost",
]);

/**
 * A move's effect (§3.5). The `before` figures are the published schedule's;
 * the `after` figures and `clears` are the re-solve's with the move applied,
 * null until the runner has scored the move (decided in P3-I-3's build,
 * 2026-10-14). A push never clears, because it changes no due date.
 */
export const ScheduleMoveEffect = z
  .object({
    late_orders_before: Count,
    late_orders_after: Count.nullable(),
    late_days_before: Count,
    late_days_after: Count.nullable(),
    clears: z.boolean().nullable(),
    scored: z.boolean(),
  })
  .openapi("ScheduleMoveEffect");

/** One stretch of closed time an overtime move opens as an `extra_shift` at factor 1.00. */
export const ScheduleOvertimeInterval = z
  .object({
    start: Instant,
    end: Instant,
    /** Effective minutes the stretch adds on the work centre. */
    added_minutes: z.number().int().min(1),
  })
  .openapi("ScheduleOvertimeInterval");

/**
 * An operation a move names, labelled for its reader (§7 narrowing (a)).
 * `op_ref` is `op:<schedule operation id>` where the label has no link, and
 * null for an operation the schedule read does not hold, which has no row to
 * serve it by (D-418).
 */
export const ScheduleMoveOperation = z
  .object({
    op_ref: z.string().nullable(),
    label: ScheduleOrderLabel,
    step_no: z.number().int(),
    /** The operation's effective minutes on the lane it leaves. */
    minutes: Minutes,
  })
  .openapi("ScheduleMoveOperation");

const moveBase = {
  move_id: Uuid,
  /** 1-based, every reader of the moves served the same order (D-402 ruling 4). */
  rank: z.number().int().min(1),
  effect: ScheduleMoveEffect,
  /**
   * The money the move spends, in minor units of `currency`, negative when an
   * alternate's rates are lower; null when unpriced. Absent by key without
   * `planning.cost.read` (§7 narrowing (b)).
   */
  cost_minor: z.number().int().nullable().optional(),
  /**
   * The standard-cost value of the orders the move makes late, in minor units
   * of `currency`; null when unpriced. Absent by key without
   * `planning.cost.read`.
   */
  value_at_risk_minor: z.number().int().min(0).nullable().optional(),
  /** The facility's currency. Absent by key without `planning.cost.read`. */
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .optional(),
  /**
   * Why the first null money field, the cost before the value, is null; null
   * when both are priced. Absent by key without `planning.cost.read`,
   * because it describes the money.
   */
  unpriced_reason: ScheduleMoveUnpricedReason.nullable().optional(),
};

/**
 * A ranked move of an overload (§3.5, D-402). Its stored `move_key`, the
 * canonical text the ranking breaks ties on, spells operation and order ids
 * and is never served (§7 narrowing (a), decided in P3-I-3's build).
 */
export const ScheduleMove = z
  .discriminatedUnion("kind", [
    z.object({
      ...moveBase,
      kind: z.literal("overtime"),
      params: z.object({
        work_center_id: Uuid,
        intervals: z.array(ScheduleOvertimeInterval).min(1),
        /** Wall minutes the intervals span, the minutes the cost is priced on. */
        wall_minutes: z.number().int().min(1),
      }),
    }),
    z.object({
      ...moveBase,
      kind: z.literal("alternate_work_center"),
      params: z.object({
        from_work_center_id: Uuid,
        to_work_center_id: Uuid,
        operations: z.array(ScheduleMoveOperation).min(1),
        /** Effective minutes moved off the overloaded work centre. */
        moved_minutes: z.number().int().min(1),
      }),
    }),
    z.object({
      ...moveBase,
      kind: z.literal("push_order"),
      params: z.object({
        work_center_id: Uuid,
        /** `work_order:<id>` or `planned_order:<id>`, null where the label has no link (§7 narrowing (a)). */
        order_ref: z.string().nullable(),
        label: ScheduleOrderLabel,
        /** The order's standard minutes on the work centre in the week. */
        standard_minutes: z.number().int().min(1),
      }),
    }),
  ])
  .openapi("ScheduleMove");

// --- overloads (row 66) --------------------------------------------------------------

/** A late or unplaced operation an overload names, labelled for its reader (§7 narrowing (a)). */
export const ScheduleOverloadOperation = z
  .object({
    op_ref: z.string(),
    label: ScheduleOrderLabel,
    product_id: Uuid,
    due_date: PlanningDate.nullable(),
    placement: SchedulePlacement,
  })
  .openapi("ScheduleOverloadOperation");

export const ScheduleOverload = z
  .object({
    id: Uuid,
    schedule_id: Uuid,
    facility_id: Uuid,
    work_center_id: Uuid,
    week_start: PlanningMonday,
    overload_minutes: z.number().int().min(1),
    /** `overload_minutes` as hours, two decimals. */
    overload_hours: ScheduleHours,
    peak_date: PlanningDate,
    /** `R` and `C` at the peak date, so the board explains the figure (D-401 ruling 2). */
    required_minutes: Minutes,
    capacity_minutes: Minutes,
    /** At most 200, sorted, beside the exact `late_op_count`. */
    late_operations: z.array(ScheduleOverloadOperation),
    late_op_count: Count,
    status: ScheduleOverloadStatus,
    /**
     * The ranked moves, at most one of each kind (P3-I-3, D-402), served to a
     * holder of `planning.schedule.publish` or `planning.cost.read`
     * at the schedule's facility and absent by key for every other reader
     * (§7 narrowing (b)).
     */
    proposed_moves: z.array(ScheduleMove).max(3).optional(),
    /** Served beside `proposed_moves` and absent with it. */
    moves_status: ScheduleMovesStatus.optional(),
    /** The move applied, its author and its time, each set exactly when `move_applied`. */
    applied_move_id: Uuid.nullable(),
    applied_by: Uuid.nullable(),
    applied_at: Instant.nullable(),
    created_at: Instant,
  })
  .openapi("ScheduleOverload");

export const ScheduleOverloadListQuery = z.object({
  work_center_id: Uuid.optional(),
  week_start: PlanningMonday.optional(),
  status: ScheduleOverloadStatus.optional(),
  cursor: PageCursor,
  limit: PageLimit,
});

export const ScheduleOverloadListResponse = z
  .object({ items: z.array(ScheduleOverload), next_cursor: z.string().nullable() })
  .openapi("ScheduleOverloadListResponse");

// --- applying a move (row 67, D-402 ruling 5) -----------------------------------------

export const ScheduleOverloadParams = z.object({ id: Uuid, overloadId: Uuid });

export const ScheduleApplyMoveRequest = z
  .object({ move_id: Uuid })
  .strict()
  .openapi("ScheduleApplyMoveRequest");

/** A commit's or an apply's answer: the header of the schedule it solved and published. */
export const ScheduleMoveResult = z
  .object({ schedule: ScheduleHeader })
  .openapi("ScheduleMoveResult");

/** 403 on an overtime apply without the calendar and cost codes, naming the codes the caller lacks. */
export const ScheduleApplyMoveForbiddenProblem = Problem.extend({
  required_codes: z.array(z.string()).optional(),
}).openapi("ScheduleApplyMoveForbiddenProblem");

/**
 * Why a move can no longer be written into the factory's data (row 67,
 * D-415 ruling 9): a step started or put on quality hold since the solve, and
 * an overtime stretch that has begun, were added in P3-I-3's audit (D-418).
 */
export const ScheduleMoveNotApplicableReason = z.enum([
  "order_closed",
  "work_center_archived",
  "operation_done",
  "operation_started",
  "operation_held",
  "overtime_past",
]);

export const ScheduleMoveNotApplicableProblem = Problem.extend({
  reason: ScheduleMoveNotApplicableReason.optional(),
}).openapi("ScheduleMoveNotApplicableProblem");

// --- the drag (rows 68 and 69, §3.6, D-403) -------------------------------------------

const UUID_TEXT = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";

/**
 * The operation a drag moves, as the board served it: its stable reference
 * `wo:<operation id>` or `po:<planned order id>:<routing step id>`, or
 * `op:<schedule operation id>` where §7 narrowing (a) served the row's own id
 * (decided in P3-I-3's build, 2026-10-14).
 */
export const ScheduleMoveOpRef = z
  .string()
  .regex(new RegExp(`^(wo:${UUID_TEXT}|po:${UUID_TEXT}:${UUID_TEXT}|op:${UUID_TEXT})$`))
  .openapi({ example: "wo:0198c097-0000-7000-8000-0000000000a1" });

/** A start with its offset on a whole minute; seconds or a fraction answer 400. */
export const ScheduleMoveStart = Instant.refine(
  (value) => /T\d{2}:\d{2}(:00(\.0+)?)?(Z|[+-]\d{2}(:?\d{2})?)$/.test(value),
  { message: "a start is a whole minute", params: { reason: "not_whole_minute" } },
).openapi({ example: "2026-10-20T08:00:00+05:00" });

const moveBody = {
  op_ref: ScheduleMoveOpRef,
  start: ScheduleMoveStart,
  /** The lane to move to: the step's work centre or one of its alternates; omitted keeps the lane. */
  work_center_id: Uuid.optional(),
};

export const ScheduleMovePreviewRequest = z
  .object(moveBody)
  .strict()
  .openapi("ScheduleMovePreviewRequest");

export const ScheduleMoveCommitRequest = z
  .object({ ...moveBody, preview_run_id: Uuid })
  .strict()
  .openapi("ScheduleMoveCommitRequest");

/** Hours with two decimals and a sign, `"-1.50"`. */
export const ScheduleSignedHours = z
  .string()
  .regex(/^-?\d{1,9}\.\d{2}$/)
  .openapi({ example: "-1.50", description: "Hours, two decimals, signed" });

/**
 * An order whose lateness the move changes. `slip_days` is its late days with
 * the move less its late days without, positive for an order newly late and
 * negative for one no longer late. An end unplaced on one side counts to the
 * day after the horizon's last date, at least one day (§3.6 step 3, D-415
 * ruling 7), so the figure is never null.
 */
export const ScheduleLateOrderChange = z
  .object({
    /** `work_order:<id>` or `planned_order:<id>`, null where the label has no link (§7 narrowing (a)). */
    order_ref: z.string().nullable(),
    label: ScheduleOrderLabel,
    due_date: PlanningDate,
    old_end: Instant.nullable(),
    new_end: Instant.nullable(),
    slip_days: z.number().int(),
    /**
     * The order is pegged to a line with a committed or at-risk promise: a
     * slip here moves a date a customer was told (D-470 ruling 3, P3-J J-3).
     */
    promise_backed: z.boolean(),
    /**
     * The promise behind it, served only to a holder of `planning.read` at
     * the schedule's facility and absent for everyone else; null when the
     * order is not promise-backed (D-470 ruling 3).
     */
    promise: z
      .object({
        promise_no: z.string(),
        status: z.enum(["committed", "at_risk"]),
      })
      .nullable()
      .optional(),
  })
  .openapi("ScheduleLateOrderChange");

/** The KPIs with the move less the KPIs without it (§2.16's figures, signed). */
export const ScheduleKpiDelta = z
  .object({
    late_orders: z.number().int(),
    total_late_days: z.number().int(),
    makespan_hours: ScheduleSignedHours,
    total_setup_minutes: z.number().int(),
    overloads: z.number().int(),
    unplaced: z.number().int(),
    batch_pulls: z.number().int(),
  })
  .openapi("ScheduleKpiDelta");

/** An overload the move would add; a preview writes no schedule, so it names no row. */
export const ScheduleNewOverload = z
  .object({ work_center_id: Uuid, week_start: PlanningMonday, overload_hours: ScheduleHours })
  .openapi("ScheduleNewOverload");

/**
 * An operation that would start before the work ahead of it in its order
 * ends. Both references are served as the board serves them, `op:<schedule
 * operation id>` where the label has no link, and null for an operation the
 * diffed schedule does not hold; `predecessor_op_ref` is also null when the
 * solve released the pin with no predecessor placed (§7 narrowing (a), D-418).
 */
export const SchedulePrecedenceConflict = z
  .object({
    op_ref: z.string().nullable(),
    predecessor_op_ref: z.string().nullable(),
    predecessor_end: Instant.nullable(),
  })
  .openapi("SchedulePrecedenceConflict");

/**
 * The consequences of a drag against the published schedule (§3.6 step 3,
 * D-403 ruling 3). Each list holds at most 200 beside its exact count. The
 * run row `preview_run_id` names records them, and a commit is judged against
 * that record.
 */
export const ScheduleMovePreview = z
  .object({
    preview_run_id: Uuid,
    /** The published schedule the consequences are diffed against. */
    schedule_id: Uuid,
    op_ref: z.string(),
    start: Instant,
    work_center_id: Uuid,
    moved_ops: z.array(ScheduleMovedOperation),
    moved_ops_count: Count,
    newly_late_orders: z.array(ScheduleLateOrderChange),
    newly_late_count: Count,
    resolved_late_orders: z.array(ScheduleLateOrderChange),
    resolved_late_count: Count,
    kpi_delta: ScheduleKpiDelta,
    new_overloads: z.array(ScheduleNewOverload),
    new_overloads_count: Count,
    /** Empty for a move the commit may save; any entry makes the commit answer 422 `MOVE_BREAKS_PRECEDENCE`. */
    precedence_conflicts: z.array(SchedulePrecedenceConflict),
  })
  .openapi("ScheduleMovePreview");

/** Why a commit found its preview stale (D-403 ruling 4, D-414 ruling 11). */
export const SchedulePreviewStaleReason = z.enum([
  "expired",
  "newly_late_changed",
  "slipped_further",
  "new_overload",
  "moved_differently",
]);

/**
 * 409 `PREVIEW_STALE` carries the fresh preview, so the planner sees what the
 * factory looks like now, and `reasons`, each test the old preview failed in
 * the order above (D-418). Row 69's 409 `SCHEDULE_SUPERSEDED` carries neither.
 */
export const SchedulePreviewStaleProblem = Problem.extend({
  preview: ScheduleMovePreview.optional(),
  reasons: z.array(SchedulePreviewStaleReason).optional(),
}).openapi("SchedulePreviewStaleProblem");

/** 422 `MOVE_BREAKS_PRECEDENCE` names the conflicts as a preview serves them (D-418). */
export const ScheduleMoveBreaksPrecedenceProblem = Problem.extend({
  precedence_conflicts: z.array(SchedulePrecedenceConflict).optional(),
}).openapi("ScheduleMoveBreaksPrecedenceProblem");

// --- the directives (rows 70 and 71, §2.19b, D-403) -----------------------------------

export const ScheduleDirectiveKind = z.enum(["pin_position", "pin_work_center", "defer"]);
export const ScheduleDirectiveStatus = z.enum(["active", "released", "expired", "removed"]);
/** Each ending's reason (§2.19b): released, expired or removed, as migration 0097 ties them. */
export const ScheduleDirectiveEndedReason = z.enum([
  "predecessor_late",
  "overlaps_fixed_work",
  "operation_done",
  "order_closed",
  "order_converted",
  "replaced",
  "unpinned",
]);
export const ScheduleDirectiveOrigin = z.enum(["commit_move", "apply_move"]);

/**
 * A planner's standing instruction (§2.19b). It carries its author's id and
 * no words (§7 narrowing (c)); its operation and order references spell ids,
 * so each is served only where the label's link opens for the reader and is
 * null otherwise, and the directive's own `id` names it (decided in P3-I-3's
 * build, 2026-10-14).
 */
export const ScheduleDirective = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    kind: ScheduleDirectiveKind,
    op_ref: z.string().nullable(),
    order_ref: z.string().nullable(),
    label: ScheduleOrderLabel,
    /** The operation's step on the published schedule; null for a defer or an operation not on it. */
    step_no: z.number().int().nullable(),
    step_name: z.string().nullable(),
    work_center_id: Uuid.nullable(),
    pin_start: Instant.nullable(),
    status: ScheduleDirectiveStatus,
    ended_reason: ScheduleDirectiveEndedReason.nullable(),
    origin: ScheduleDirectiveOrigin,
    /** The overload whose applied move wrote it; null for a commit. */
    schedule_overload_id: Uuid.nullable(),
    created_by: Uuid,
    created_at: Instant,
    /** Null for the system. */
    ended_by: Uuid.nullable(),
    ended_at: Instant.nullable(),
  })
  .openapi("ScheduleDirective");

export const ScheduleDirectiveListQuery = z.object({
  facility_id: Uuid,
  /** `active` when omitted. */
  status: ScheduleDirectiveStatus.optional(),
  kind: ScheduleDirectiveKind.optional(),
  cursor: PageCursor,
  limit: PageLimit,
});

export const ScheduleDirectiveListResponse = z
  .object({ items: z.array(ScheduleDirective), next_cursor: z.string().nullable() })
  .openapi("ScheduleDirectiveListResponse");

export const ScheduleDirectiveIdParam = z.object({ id: Uuid });

/** 409 `SCHEDULE_DIRECTIVE_NOT_ACTIVE` names the status the directive ended in. */
export const ScheduleDirectiveConflictProblem = Problem.extend({
  directive_status: ScheduleDirectiveStatus.optional(),
}).openapi("ScheduleDirectiveConflictProblem");

// --- audit actions (§5's P3-I audit rows) ---------------------------------------------

export const SCHEDULE_AUDIT_ACTIONS = {
  setupMatrixReplaced: "setup_matrix.replaced",
  scheduleSolveRequested: "schedule.solve_requested",
  schedulePublished: "schedule.published",
  scheduleDiscarded: "schedule.discarded",
  // P3-I-3 (D-402 ruling 5, D-403 rulings 4 and 5).
  scheduleMoveCommitted: "schedule.move_committed",
  scheduleDirectiveRemoved: "schedule_directive.removed",
  scheduleOverloadMoveApplied: "schedule_overload.move_applied",
} as const;
